import './ninja-cut.css';

// The engine ships as a global (window.NinjaCutGame) loaded via a <script> tag — not as an
// ES module — mirroring how peroni-f1-2026 integrates sepho-race and how the standalone
// build (../sephora-games/standalone/ninja-cut) wires it.
const SCRIPT_URL = '/build/games/ninja-cut/ninja-cut.min.js';

// ── Image assets ──────────────────────────────────────────────────────────────
// Imported through webpack so they get hashed/published. require.context keeps the
// good/bad mapping declarative: drop or rename a sprite and adjust the lists below.
// Each good fruit pairs with its own splash set (assets/games/ninja-cut/images/splashe/
// <fruit>-N.webp), so a sliced fruit bursts into its matching debris.
const spriteCtx = require.context('./images', false, /\.(webp|svg|png)$/);
const splashCtx = require.context('./images/splashe', false, /\.webp$/);

const resolveAsset = (mod) => (mod && mod.default) ? mod.default : mod;

const sprites = {};
spriteCtx.keys().forEach((key) => {
    const name = key.replace('./', '').replace(/\.(webp|svg|png)$/, '');
    sprites[name] = resolveAsset(spriteCtx(key));
});

const splashes = {};
splashCtx.keys().forEach((key) => {
    const file = key.replace('./', '');          // e.g. "mango-9.webp"
    const fruit = file.split('-')[0];            // e.g. "mango"
    (splashes[fruit] = splashes[fruit] || []).push(resolveAsset(splashCtx(key)));
});

// Branding mapping — fruits are "good", chmiel is the "bonus" (worth pointsPerBonus),
// the bottle cap is "bad". Each entry pairs with its own splash set built above.
const GOOD_FRUITS = ['mango', 'truskawka', 'granat', 'jablko', 'cytryna', 'czeresnia'];
const BONUS_FRUITS = ['chmiel'];
const BAD_OBJECTS = ['kapsel'];

// autoStart: true → the engine runs straight into play; the surrounding modal owns the
// loading / instructions / result / answer screens (see the modal handler).
const GAME_CONFIG = {
    duration: 60,
    lives: 3,
    autoStart: true,

    maxPlayWidth: 1080,
    maxPlayHeight: 1920,

    gravity: 1300,
    launchSpeedMin: 900,
    launchSpeedMax: 1250,
    lateralSpeedMax: 260,

    spawnInterval: 1.1,
    spawnJitter: 0.5,
    maxPerWave: 3,
    badChance: 0.28,
    bonusChance: 0.08, // chmiel (bonus) spawn probability; badChance + bonusChance must stay < 1
    objectRadius: 46,
    spawnMargin: 0.12,
    difficultyRamp: {
        spawnIntervalScale: 0.6,
        speedScale: 1.15,
    },

    pointsPerGood: 10,
    pointsPerBonus: 50,
    comboWindow: 0.35,
    comboBonus: 2,
    maxCombo: 5,

    trailMaxPoints: 16,
    trailMaxAge: 0.16,
    bladeWidth: 16,

    halfLifetime: 0.9,
    halfSeparation: 140,
    halfSpin: 6,

    popupLifetime: 0.8,

    heartbeatInterval: 10000,
};

// Branding / assets config — texts & colors from config.assets.js, image mapping built above.
function buildAssetsConfig(demo) {
    return {
        theme: 'lech',
        texts: {
            gameTitle: 'NinjaCut',
            gameSubtitle: 'Tnij dobre, omijaj złe!',
            startButton: 'Graj',
            loading: 'Ładowanie...',
            hudScore: 'Wynik',
            hudTime: 'Czas',
            hudLives: 'Życia',
            comboToast: 'Combo x{combo}!',
            resultTitle: 'Koniec gry',
            resultTimeUp: 'Czas minął!',
            resultFail: 'Brak żyć!',
            resultScore: 'Twój wynik',
            playAgain: 'Zagraj ponownie',
        },
        // Demo mode shows a "PRÓBA" label instead of the score pill. No timer in the HUD.
        hud: demo
            ? [{type: 'text', text: 'PRÓBA'}, {type: 'lives'}]
            : [{type: 'score', label: 'PKT'}, {type: 'lives'}],
        images: {
            background: sprites['background'] ?? null,
            good: GOOD_FRUITS.filter((f) => sprites[f]).map((f) => ({img: sprites[f], splash: splashes[f] ?? []})),
            bad: BAD_OBJECTS.filter((b) => sprites[b]).map((b) => ({img: sprites[b]})),
            bonus: BONUS_FRUITS.filter((f) => sprites[f]).map((f) => ({img: sprites[f], splash: splashes[f] ?? []})),
            heartFull: sprites['life-full'] ?? null,
            heartEmpty: sprites['life-empty'] ?? null,
        },
        sounds: {
            slice: null,
            bomb: null,
        },
        tokens: {
            fontFamily: "'Lech Sans', 'Arial Black', sans-serif",
        },
        colors: {
            bgStart: '#0c5715',
            bgEnd: '#0c5715',
            blade: '#FFFFFF',
            bladeGlow: '#80FFF6',
            good: '#FF66CC',
            bad: '#212121',
            scorePopup: '#FFF9C4',
            primary: 'white',
        },
    };
}

function apiBase(uuid) {
    return `/api/application/${uuid}/ninja-cut`;
}

/**
 * Preload every game image so the canvas has them ready before the first frame.
 * Sprites (objects + HUD) load first, then the heavier splash debris set. `onProgress`
 * is called with a 0..1 fraction as each image settles (load or error), driving the
 * loading screen's 10 life-indicator progress bar. Resolves once everything settled.
 */
export function preloadNinjaCutAssets(onProgress = () => {
}) {
    const spriteUrls = Object.values(sprites);
    const splashUrls = Object.values(splashes).reduce((acc, list) => acc.concat(list), []);
    const ordered = [...spriteUrls, ...splashUrls];
    const total = ordered.length || 1;
    let settled = 0;

    const settle = () => {
        settled += 1;
        onProgress(Math.min(1, settled / total));
    };

    const loadOne = (url) => new Promise((res) => {
        const img = new Image();
        img.onload = () => {
            settle();
            res();
        };
        img.onerror = () => {
            settle();
            res();
        };
        img.src = url;
    });

    return (async () => {
        // Object sprites first (sequential — small set, keeps the bar moving early)…
        for (const url of spriteUrls) {
            await loadOne(url);
        }
        // …then the splash debris in parallel (large set, order irrelevant).
        await Promise.all(splashUrls.map(loadOne));
        onProgress(1);
    })();
}

let gameInstance = null;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (window.NinjaCutGame || document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

/**
 * Mount and run the game inside `container`.
 *  - demo === true  → no score registration, no heartbeats (mock API, PRÓBA HUD).
 *  - demo === false → registers the score + sends heartbeats to the backend.
 *
 * Ninja Cut posts no per-cut events — the engine's apiClient handles start / heartbeat /
 * result, so (unlike sepho-race) there are no gameplay events to forward.
 * `onDone(score)` is invoked once the game finishes (instance already torn down).
 */
export function startNinjaCut(container, {
    demo = false, uuid = null, onDone = () => {
    }, onError = null
} = {}) {
    teardownNinjaCut();
    container.classList.add('nc-game');

    // Trial runs up to 3 min, the real game up to 10 min (the engine counts down from duration).
    const gameConfig = {...GAME_CONFIG, duration: demo ? 180 : 600};

    const apiConfig = demo
        // Demo: mock API with no sessionToken → engine heartbeats stay disabled.
        ? {useMock: true, mockDelay: 0, mockResponses: {startGame: {}, submitResult: {}}}
        : {
            useMock: false,
            endpoints: {
                startGame: `${apiBase(uuid)}/start`,
                heartbeat: `${apiBase(uuid)}/heartbeat`,
                submitResult: `${apiBase(uuid)}/result`,
            },
        };

    loadScript(SCRIPT_URL)
        .then(() => {
            if (!window.NinjaCutGame) {
                console.error('NinjaCutGame: script loaded but global is not defined');
                onDone(0);
                return;
            }

            gameInstance = window.NinjaCutGame.init(container.id, {
                assetsConfig: buildAssetsConfig(demo),
                gameConfig,
                apiConfig,
                callbacks: {
                    onComplete: (result) => {
                        const score = result?.score ?? gameInstance?._score ?? 0;
                        // reason: 'timeup' | 'nolives' | 'forced' — lets the modal tailor the result copy.
                        const reason = result?.reason ?? null;
                        teardownNinjaCut();
                        onDone(score, reason);
                    },
                    onError: (error) => {
                        console.error('NinjaCutGame error:', error);
                        teardownNinjaCut();
                        // A failed /start (e.g. outside play hours, already played) surfaces here as an
                        // ApiError carrying { status, data }. Hand it to the caller so it can show a
                        // pretty screen; fall back to the neutral score-0 result if unhandled.
                        if (onError) {
                            onError(error);
                        } else {
                            onDone(0);
                        }
                    },
                },
            });
        })
        .catch((e) => {
            console.error('NinjaCutGame: failed to load game script', e);
            onDone(0);
        });
}

export function teardownNinjaCut() {
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */
        }
        gameInstance = null;
    }
}

/** Pause a running game (no-op unless the engine is in the "playing" state). */
export function pauseNinjaCut() {
    try {
        gameInstance?.pause?.();
    } catch { /* ignore */
    }
}

/** Resume a paused game (no-op unless the engine is in the "paused" state). */
export function resumeNinjaCut() {
    try {
        gameInstance?.resume?.();
    } catch { /* ignore */
    }
}
